import { useMemo, useRef } from 'react';
import { useField, useForm, SubmitResult } from '@shopify/react-form';
import validateNumber, {
  ValidateOptions,
  ValidNumberOption,
} from '@sdk/utils/validation/validateNumber';
import { OrgSettings } from '@sdk/authentication/types/OrganisationType';
import { PersonasDict } from 'sling-sdk/src/personaCollection/types/PersonasDict';
import useSlingApi from '@sdk/api/useSlingApi';
import getErrorMessages from 'sling-sdk/src/utils/getErrorMessages';

import { decorateSubmit } from '@web/forms/utils/decorateSubmit';
import { FormSubmitStatuses } from '@web/forms/types';

import normalizeOrgSettings from '../../../../settings/helpers/normalizeOrgSettings';
import preSaveUserOrgSettingsHandler from '../../../../settings/helpers/preSaveUserOrgSettingsHandler';
import { UpdateOrgSettings } from '../../onboardingChecklist.types';

type Props = {
  orgSettings: OrgSettings;
  personasDict: PersonasDict;
  updateOrgSettings: UpdateOrgSettings;
};

const useOrgSettingsForm = ({ orgSettings, personasDict, updateOrgSettings }: Props) => {
  const userOrgSettings = useMemo(() => orgSettings, [orgSettings]);
  const api = useSlingApi();

  const submitPromise = useRef<Promise<SubmitResult>>();
  const {
    fields,
    submit: formSubmit,
    dirty,
    reset,
    submitErrors,
  } = useForm({
    fields: {
      firstDayOfWeek: useField(userOrgSettings.firstDayOfWeek),
      usersCanSeeOtherSchedules: useField(userOrgSettings.usersCanSeeOtherSchedules),
      restBreakEnabled: useField(userOrgSettings.restBreakEnabled),
      restBreakThreshold: useField({
        value: userOrgSettings.restBreakThreshold,
        validates: (value) => {
          if (
            !validateNumber(value, {
              [ValidateOptions.validNumber]: ValidNumberOption.nonNegative,
              [ValidateOptions.integer]: true,
            })
          ) {
            return 'restBreakThreshold invalid';
          }
        },
      }),
      usersCanSeeSchedulesForOtherPositions: useField(
        userOrgSettings.usersCanSeeSchedulesForOtherPositions
      ),
      usersCanSeeSchedulesInOtherLocations: useField(
        userOrgSettings.usersCanSeeSchedulesInOtherLocations
      ),
    },
    onSubmit: async (fieldValues: Partial<OrgSettings>) => {
      try {
        const payload = preSaveUserOrgSettingsHandler({
          ...userOrgSettings,
          ...fieldValues,
        });

        const { data } = await api.auth.setUserOrgSettings(payload);
        const normalizedSettings = normalizeOrgSettings(data, personasDict);
        updateOrgSettings({ settings: normalizedSettings });

        submitPromise.current = Promise.resolve({ status: FormSubmitStatuses.success });
        return submitPromise.current;
      } catch (error) {
        const [errorMsg] = getErrorMessages(error);

        submitPromise.current = Promise.resolve({
          status: FormSubmitStatuses.fail,
          errors: [{ message: errorMsg }],
        });
        return submitPromise.current;
      }
    },
  });

  const submit = decorateSubmit(formSubmit, submitPromise);

  return { fields, submit, dirty, reset, submitErrors };
};

export default useOrgSettingsForm;
